import {forwardRef} from 'react';

import {TooltipRenderProps} from 'react-joyride';

import CloseIcon from '@mui/icons-material/Close';
import {Box, Button, IconButton, Stack, Typography} from '@mui/material';

import {greenColors, orangeColors} from '../../layouts/theme/designSystem';

export const WalkthroughTooltip = forwardRef<HTMLDivElement, TooltipRenderProps>((props, ref) => {
  const {
    backProps,
    closeProps,
    primaryProps,
    skipProps,
    tooltipProps,
    step,
    index,
    size,
    isLastStep,
    continuous,
  } = props;

  const showBack = index > 0;
  const showSkip = !isLastStep;

  return (
    <Box
      ref={ref}
      {...tooltipProps}
      sx={{
        bgcolor: 'background.paper',
        border: `1px solid ${orangeColors[3]}`,
        borderRadius: '12px',
        boxShadow: '0 8px 24px rgba(9, 44, 76, 0.16)',
        maxWidth: 380,
        minWidth: 280,
        p: 2.5,
        outline: 'none',
      }}
    >
      <Stack direction='row' alignItems='flex-start' justifyContent='space-between' spacing={1} sx={{mb: 1}}>
        {step.title ? (
          <Typography
            component='h3'
            sx={{
              fontSize: '16px',
              fontWeight: 700,
              lineHeight: 1.4,
              color: greenColors[700],
              flex: 1,
            }}
          >
            {step.title}
          </Typography>
        ) : (
          <Box sx={{flex: 1}} />
        )}
        <IconButton
          {...closeProps}
          size='small'
          sx={{color: greenColors[700], mt: -0.5, mr: -0.5}}
        >
          <CloseIcon fontSize='small' />
        </IconButton>
      </Stack>

      <Box sx={{color: greenColors[700], fontSize: '14px', lineHeight: 1.6, mb: 2}}>{step.content}</Box>

      <Stack direction='row' alignItems='center' justifyContent='space-between' spacing={1}>
        <Typography sx={{fontSize: '12px', color: greenColors[100], fontWeight: 600}}>
          {index + 1} / {size}
        </Typography>
        <Stack direction='row' spacing={1}>
          {showSkip && (
            <Button
              {...skipProps}
              variant='text'
              size='small'
              sx={{
                color: greenColors[100],
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              {skipProps.title}
            </Button>
          )}
          {showBack && (
            <Button
              {...backProps}
              variant='outlined'
              size='small'
              sx={{
                borderColor: greenColors[700],
                color: greenColors[700],
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '8px',
              }}
            >
              {backProps.title}
            </Button>
          )}
          <Button
            {...primaryProps}
            variant='contained'
            size='small'
            sx={{
              bgcolor: greenColors[700],
              color: '#fff',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              '&:hover': {bgcolor: greenColors[600]},
            }}
          >
            {continuous && !isLastStep ? primaryProps.title : primaryProps.title}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
});

WalkthroughTooltip.displayName = 'WalkthroughTooltip';
